import type { TFunction } from "i18next";
import type { U2000Condition } from "../../u2000Core/components/tables/basic/U2000BasicSearch";
import type { U2000TableColumn } from "../../u2000Core/components/tables/basic/U2000BasicTableList";
import type { Criteria, U2000SearchParameter } from "../../u2000Core/reducer/formReducer/U2000SearchState";
import type { U2000SearchStateHandlersReturn } from "../../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import { getCurrentWeekMondayIsoLocal, getPreviousWeekMondayIsoLocal } from "./U3130PeriodPresets";
import { u3130FormatPeriodDateRange } from "./u3130FormatPeriodDateRange";

const NS = "u3130";

/** Préréglages liste approbation U3130 (alignés règles métier statut + période). */
export type U3130ApprovalListPreset =
  | "waiting_for_approval"
  | "processed_current_week"
  | "processed_previous_week"
  | "processed_all_weeks";

/** Valeur envoyée au backend en complément des criterias (contrat API U3130 / search). */
export type U3130ApprovalListPresetApi =
  | "WAITING_FOR_APPROVAL"
  | "PROCESSED_CURRENT_WEEK"
  | "PROCESSED_PREVIOUS_WEEK"
  | "PROCESSED_ALL_WEEKS";

/** Statuts « traités » (valeur critère `in`, liste CSV) : Approve, WaitingForGenerateTEC, Close. */
export const U3130_PROCESSED_TIMESHEET_STATUSES = ["Approve", "WaitingForGenerateTEC", "Close"] as const;

export const U3130_PROCESSED_STATUS_IN_VALUE = U3130_PROCESSED_TIMESHEET_STATUSES.join(",");

function newCriteriaId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2);
}

export function u3130MapApprovalListPresetToApi(preset: U3130ApprovalListPreset): U3130ApprovalListPresetApi {
  switch (preset) {
    case "waiting_for_approval":
      return "WAITING_FOR_APPROVAL";
    case "processed_current_week":
      return "PROCESSED_CURRENT_WEEK";
    case "processed_previous_week":
      return "PROCESSED_PREVIOUS_WEEK";
    case "processed_all_weeks":
      return "PROCESSED_ALL_WEEKS";
    default:
      return "WAITING_FOR_APPROVAL";
  }
}

function equalsCondition(t: TFunction): U2000Condition {
  return {
    name: "equals",
    label: t("lblCndEquals", { ns: "common", defaultValue: "Égal" }),
    isRequiredValue: true,
  };
}

function inListCondition(t: TFunction): U2000Condition {
  return {
    name: "in",
    label: t("lblCndIn", { ns: "common", defaultValue: "Parmi" }),
    isRequiredValue: true,
  };
}

function mkCriteria(
  field: U2000TableColumn,
  condition: U2000Condition,
  value: string,
  timestamps: number,
  displayValue?: string,
): Criteria {
  return {
    id: newCriteriaId(),
    field,
    condition,
    value,
    displayValue,
    timestamps,
  };
}

function u3130StatusLabel(t: TFunction, status: string): string {
  return t(`status${status}`, { ns: NS, defaultValue: status });
}

function u3130ProcessedStatusDisplayValue(t: TFunction): string {
  return U3130_PROCESSED_TIMESHEET_STATUSES.map((s) => u3130StatusLabel(t, s)).join(", ");
}

/**
 * Construit les critères pour le préréglage, les écrit dans le state (pour l'UI)
 * **et renvoie un `U2000SearchParameter` prêt à envoyer au backend**
 * afin d'éviter toute lecture d'un state potentiellement périmé (closure React).
 */
export function u3130ApplyApprovalListPresetToSearchState(
  preset: U3130ApprovalListPreset,
  searchStateHandlers: U2000SearchStateHandlersReturn,
  t: TFunction,
): U2000SearchParameter | null {
  const columns = searchStateHandlers.getColumns();
  const statusCol = columns.find((c) => c.name === "status");
  const periodCol = columns.find((c) => c.name === "periodDate");
  if (!statusCol) return null;

  const eq = equalsCondition(t);
  const inn = inListCondition(t);
  const now = Date.now();

  const retain = searchStateHandlers
    .getCriterias()
    .filter((c) => c.field.name !== "periodDate" && c.field.name !== "status");

  const next: Criteria[] = [...retain];

  if (preset === "waiting_for_approval") {
    next.push(mkCriteria(statusCol, eq, "WaitingForApproval", now, u3130StatusLabel(t, "WaitingForApproval")));
  } else {
    next.push(mkCriteria(statusCol, inn, U3130_PROCESSED_STATUS_IN_VALUE, now, u3130ProcessedStatusDisplayValue(t)));
    if (periodCol) {
      if (preset === "processed_current_week") {
        const periodYmd = getCurrentWeekMondayIsoLocal();
        next.push(mkCriteria(periodCol, eq, periodYmd, now, u3130FormatPeriodDateRange(periodYmd)));
      } else if (preset === "processed_previous_week") {
        const periodYmd = getPreviousWeekMondayIsoLocal();
        next.push(mkCriteria(periodCol, eq, periodYmd, now, u3130FormatPeriodDateRange(periodYmd)));
      }
    }
  }

  searchStateHandlers.setCriterias(next);

  const result: U2000SearchParameter = {
    criterias: next.map((c) => ({
      field: c.field.name,
      operator: c.condition.name,
      value: c.value,
      type: c.field.type,
    })),
    fields: searchStateHandlers.getFields().map((f) => ({ name: f.field.name })),
    sortedFields: searchStateHandlers.getSortedFields().map((s) => ({
      name: s.field.name,
      sortOrder: (s.sortOrder as string) || "ASC",
    })),
  };

  return result;
}
